import { selector } from 'recoil';
import type { Utils } from '@sparrow-marketing/contracts';

import {
  FilterAccountsState,
  FilterConfigurationState,
  FilterInputValueState,
  HideInActiveState,
  SelectedFilterAccountsState,
} from './states';

import type { GetFilterSelector } from './types';

export const FilterAccountSelector = selector<GetFilterSelector>({
  key: 'selected-account-selector',
  get: ({ get }) => {
    // grab all the filter state
    const type = /website/gi.test(window.location.pathname) ? 'website' : 'ads';
    const filterConfiguration = get(FilterConfigurationState);
    const filterAccounts = get(FilterAccountsState);
    const selectedFilterAccountIds = get(SelectedFilterAccountsState);
    const hideInActiveCampaign = get(HideInActiveState);
    const filterInputValue = get(FilterInputValueState);

    const { sourceType, sourceId } = filterConfiguration[type];

    // get the initial accounts
    const accounts = filterAccounts?.[sourceType]?.[sourceId] ?? {};

    // get the selected account ids
    const selectedAccounts = selectedFilterAccountIds?.[sourceId] ?? {};

    // get the keys
    const accountKeys = Object.keys(accounts);

    // create an object
    const tempFilteredAccounts: Utils.DynamicObject = {
      ...accountKeys,
    };

    // delete the unnecessary keys from the object
    Object.keys(tempFilteredAccounts).forEach(key => {
      if (typeof tempFilteredAccounts[key] !== 'object') {
        delete tempFilteredAccounts[key];
      }
    });

    // loop over all the account keys
    accountKeys.forEach(accountKey => {
      // skip the 'pages' keys is encountered
      if (accountKey === 'pages') {
        return;
      }

      // get the current accounts
      const currentAccounts = accounts[accountKey];

      // get the current selected accounts
      const currentSelectedAccountIds: string[] = selectedAccounts[accountKey];

      // set the status of filtered account based on the
      // selected account ids array
      const filteredCurrentAccounts = currentAccounts.map(
        (currentAccount: any) => {
          const accId = currentAccount?.attributes?.id?.value;
          if (
            currentSelectedAccountIds?.length > 0 &&
            currentSelectedAccountIds.includes(accId)
          ) {
            return {
              ...currentAccount,
              isActive: true,
            };
          }
          return {
            ...currentAccount,
            isActive: false,
          };
        }
      );
      tempFilteredAccounts[accountKey] = filteredCurrentAccounts;
    });

    if (/googleAnalytics/gi.test(sourceType)) {
      const { properties, views } = tempFilteredAccounts;

      // store properties
      let tempProperties = properties;

      // get all the property ids
      const propertyIds =
        properties
          ?.filter((property: any) => property.isActive)
          ?.map((property: any) => property?.attributes?.id?.value) ?? [];

      const search = filterInputValue[sourceId];
      // if search exists i.e. filter the keyword from properties
      if (search) {
        tempProperties = properties
          ?.filter((property: any) => {
            return property?.attributes?.name?.value
              .toLowerCase()
              .includes(search.toLowerCase());
          })
          ?.map((property: any) => property);
      }

      // get all the property ids
      const filterPropertyIds = properties
        ?.filter((property: any) => {
          // if search exists
          if (search) {
            return property?.attributes?.name?.value
              .toLowerCase()
              .includes(filterInputValue[sourceId].toLowerCase());
          }
          return false;
        })
        ?.map((property: any) => property?.attributes?.id?.value);

      // get all the views
      const tempViews = views
        ?.map((view: any) => {
          const viewPropertyId = view?.propertyAttributes?.id?.value;
          if (search) {
            // if search is in the view property
            if (filterPropertyIds.includes(viewPropertyId)) {
              return view;
            }
            return null;
          }
          // if views property exists in property id
          if (propertyIds.includes(viewPropertyId)) {
            return view;
          }
          return null;
        })
        ?.filter((view: any) => view);

      tempFilteredAccounts.properties = tempProperties;
      tempFilteredAccounts.views = tempViews;
    } else {
      const { campaigns, adSets, ads } = tempFilteredAccounts;

      // get all the campaign ids
      const campaignIds: string[] = campaigns
        ?.filter((campaign: any) => campaign.isActive)
        ?.map((campaign: any) => campaign?.attributes?.id?.value);

      // get all the inactive campaign ids
      const inactiveCampaignIds = campaigns
        ?.filter((campaign: any) =>
          /inactive/gi.test(campaign?.attributes?.status?.value)
        )
        ?.map((campaign: any) => campaign?.attributes?.id?.value);

      const search = filterInputValue[sourceId];

      const hideInActive = hideInActiveCampaign[sourceId];

      // filter campaign ids
      const filterCampaignIds = campaigns
        ?.filter((campaign: any) => {
          const campaignName = campaign?.attributes?.name?.value;
          const campaignStatus = campaign?.attributes?.status?.value;
          if (hideInActive && /inactive/gi.test(campaignStatus)) {
            return false;
          }
          if (search) {
            return campaignName.toLowerCase().includes(search.toLowerCase());
          }
          return true;
        })
        ?.map((campaign: any) => campaign?.attributes?.id?.value);

      // store all the campaigns
      let filterCampaign = campaigns;

      // if hide inactive is true
      if (hideInActive) {
        filterCampaign = campaigns?.filter(
          (campaign: any) =>
            !/inactive/gi.test(campaign?.attributes?.status?.value)
        );
      }

      // if search is true
      if (search) {
        filterCampaign = filterCampaign?.filter((campaign: any) =>
          campaign.attributes.name.value
            .toLowerCase()
            .includes(search.toLowerCase())
        );
      }

      const tempAdSetIds: string[] = [];

      const tempAdSets = adSets
        ?.map((adSet: any) => {
          const adSetCampaignId = adSet?.attributes?.campaign_id?.value;
          const adSetId = adSet?.attributes?.id?.value;
          const adSetCampId = adSetCampaignId.split('/').slice(-1)[0];
          // if hideInActive is true then hide the adSet for the campaign
          if (hideInActive && inactiveCampaignIds.includes(adSetCampaignId)) {
            return null;
          }

          // if search is true
          if (search) {
            // filter search
            if (filterCampaignIds.includes(adSetCampId)) {
              return adSet;
            }
            return null;
          }

          if (campaignIds.includes(adSetCampId)) {
            if (adSet.isActive) {
              tempAdSetIds.push(adSetId);
            }
            return adSet;
          }
          return null;
        })
        ?.filter((adSet: any) => adSet);

      const tempAds = ads
        ?.map((ad: any) => {
          const adCampaignId = ad?.attributes?.campaign_id?.value;
          const adAdSetId = ad?.attributes?.adset_id?.value;
          const adCampId = adCampaignId?.split?.('/')?.slice?.(-1)?.[0];
          const adAdsetId = adAdSetId?.split?.('/')?.slice?.(-1)?.[0];

          // if hideInActive is true then hide the ad for the campaign
          if (hideInActive && inactiveCampaignIds.includes(adCampId)) {
            return null;
          }

          // if search exists
          if (search) {
            // get campaign ids
            if (filterCampaignIds.includes(adCampId)) {
              return ad;
            }
            return null;
          }

          // if adSet ids include ad adSet id
          if (tempAdSetIds.includes(adAdsetId)) {
            return ad;
          }
          return null;
        })
        ?.filter((ad: any) => ad);

      tempFilteredAccounts.campaigns = filterCampaign;
      tempFilteredAccounts.adSets = tempAdSets;
      tempFilteredAccounts.ads = tempAds;
    }

    return tempFilteredAccounts;
  },
});
